import { TITLE } from "@/apollo/queries/title";
import BlurImageBackground from "@/components/BlurImageBackground";
import ButtonMultiClick from "@/components/Buttons/ButtonMultiClick";
import { FavoriteButton, ShareButton } from "@/components/Buttons/index";
import TitleMetaTags, { tags } from "@/components/MetaTags/TitleMetaTags";
import TabMenu from "@/components/TabMenu";
import TitleDetailModal from "@/components/TitleDetailModal";
import TvShowEpisodesList from "@/components/TvShowEpisodesList";
import {
  useBreadcrumbs,
  useCheckUrlHash,
  useModal,
  useTitleQuery,
} from "@/hooks";
import config, { getStoreId } from "@/lib/config";
import isShowValuePlanLabel from "@/lib/isShowValuePlanLabel";
import routes from "@/lib/routes";
import withMiddlewares from "@/middlewares/withMiddlewares";
import contentsStyles from "@/styles/Contents.module.scss";
import styles from "@/styles/Title.module.scss";
import Image from "next/legacy/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { useCallback, useEffect, useMemo } from "react";

export default function Title() {
  useCheckUrlHash("recommend");
  const router = useRouter();
  const id = Number(router.query.id);
  const hash = window.location.hash.substring(1);
  const { showModal } = useModal();
  const { setBreadcrumbs } = useBreadcrumbs();

  const { data } = useTitleQuery({
    variables: { id, withBookmark: true, withCourse: true },
  });

  const title = data?.title;
  const bookmarkMeta = title?.bookmark?.bookmark_meta;
  const bookmarkPosition = title?.bookmark?.position || 0;

  const channelId = Number(title?.channel?.id);
  const showValuePlanLabel = isShowValuePlanLabel({ channelId });

  const selectMenu = useMemo(() => {
    if (hash === "recommend") {
      return "recommend";
    } else {
      return "episodes";
    }
  }, [hash]);

  const progress = useMemo(() => {
    if (bookmarkMeta) {
      return (bookmarkPosition / bookmarkMeta.duration) * 100;
    } else {
      return 0;
    }
  }, [bookmarkMeta, bookmarkPosition]);

  const tabMenus = [
    {
      text: "エピソード一覧",
      key: "episodes",
      link: {
        href: routes.title(id),
        shallow: true,
        matchtype: "partial",
      },
    },
  ];

  const handleClick = useCallback(() => {
    showModal(<TitleDetailModal title={title} />, { style: "full" });
  }, [title]);

  useEffect(() => {
    const storeId = getStoreId();
    const valuePlanId = config.valuePlanId.vod;
    const breadcrumbs = [{ text: "ホーム", href: routes.home }];
    const channelId = parseInt(title?.channel?.id, 10);

    if (title?.channel && storeId !== channelId && valuePlanId !== channelId) {
      breadcrumbs.push({
        text: title.channel.name,
        href: routes.channelDetail(channelId),
      });
    }
    breadcrumbs.push({ text: title.name });
    setBreadcrumbs(breadcrumbs);
  }, [title]);

  return (
    <>
      <TitleMetaTags title={title} />
      <div className={styles.container}>
        <div className={styles.title}>
          <BlurImageBackground className={"title"} item={title}>
            <div className={styles.content}>
              <div className={styles.info}>
                <h1 className={styles.title}>{title.name}</h1>
                <p className={styles.description}>{title.description}</p>
                <div className={styles.buttons}>
                  <ButtonMultiClick borderLight medium onClick={handleClick}>
                    詳細を見る
                  </ButtonMultiClick>
                  <FavoriteButton borderLight transparent metaId={title.id} />
                  <ShareButton borderLight transparent title={title.name} />
                </div>
                {bookmarkMeta && (
                  <Link href={routes.watch(bookmarkMeta.id)}>
                    <div className={styles.bookmark}>
                      <div className={styles.artwork}>
                        <div className={styles.inner}>
                          {!!bookmarkMeta?.thumbnailUrl && (
                            <Image
                              src={bookmarkMeta.thumbnailUrl}
                              alt={bookmarkMeta.name}
                              layout="fill"
                              objectFit="contain"
                            />
                          )}
                          {showValuePlanLabel && (
                            <div className={styles.valueplanLabel}>
                              <Image
                                src={"/images/valueplan_label.png"}
                                alt={"valueplan_label"}
                                layout="fill"
                                objectFit="cover"
                              ></Image>
                            </div>
                          )}
                        </div>
                        <span className={styles.progressbar}>
                          <span
                            className={styles["progressbar-inner"]}
                            style={{ width: `${progress}%` }}
                          ></span>
                        </span>
                      </div>
                      <div className={styles.bookmarkTitle}>
                        <span title={bookmarkMeta.name}>
                          {bookmarkMeta.name}
                        </span>
                        <span>
                          {bookmarkPosition > 0 && "続きから"}再生する
                        </span>
                      </div>
                    </div>
                  </Link>
                )}
              </div>
              <div className={styles.artwork}>
                <div className={styles.inner}>
                  {!!title.thumbnailUrl && (
                    <Image
                      src={title.thumbnailUrl}
                      alt={title.name}
                      layout="fill"
                      objectFit="contain"
                    />
                  )}
                  {showValuePlanLabel && (
                    <>
                      <div className={styles.valueplanLabel}>
                        <Image
                          src={"/images/valueplan_label.png"}
                          alt={"valueplan_label"}
                          layout="fill"
                          objectFit="cover"
                        ></Image>
                      </div>
                      <div className={styles.leminoLogo}>
                        <Image
                          src={"/images/lemino_logo.png"}
                          alt={"lemino_logo"}
                          layout="fill"
                          objectFit="contain"
                        ></Image>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </BlurImageBackground>
        </div>

        <div className={contentsStyles.container}>
          <nav>
            <TabMenu menus={tabMenus} />
          </nav>
          <div>
            {selectMenu === "episodes" && <TvShowEpisodesList id={id} />}
          </div>
        </div>
      </div>
    </>
  );
}

export const getServerSideProps = withMiddlewares(
  async ({ req, query }, { serverProps, error, apolloClient }) => {
    const id = parseInt(query?.id, 10);
    if (isNaN(id)) {
      return { notFound: true };
    }
    const {
      data: { title, children },
    } = await apolloClient.query({
      query: TITLE,
      variables: {
        id,
        withBookmark: true,
        withCourse: true,
        withChildren: true,
      },
      fetchPolicy: "network-only",
    });

    if (title) {
      if (children && children?.pageInfo?.totalCount === 1) {
        const watchId = children.data[0].id;
        if (!!watchId) {
          return {
            redirect: {
              permanent: false,
              destination: routes.watch(watchId),
            },
          };
        } else {
          return {
            props: {
              ...serverProps,
              error,
              initialApolloState: apolloClient.cache.extract(),
            },
          };
        }
      } else {
        const metatags = tags(title);
        return {
          props: {
            ...serverProps,
            error,
            metatags,
            initialApolloState: apolloClient.cache.extract(),
          },
        };
      }
    } else {
      return { notFound: true };
    }
  }
);
